import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, workspaceAPI } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import VmixInstancesSection from './VmixInstancesSection';
import ConnectClaudeSection from './ConnectClaudeSection';
import GuestsSection from './GuestsSection';

function formatDate(d) {
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

// What the open workspace is, your place in it, and its guests. Members, seats
// and creating workspaces belong to the account — see pages/Account.jsx.
function AccessSummary({ workspace }) {
  if (workspace.your_role === 'guest') {
    return (
      <div className="text-xs text-gray-500 leading-relaxed space-y-1">
        <p>
          You are a <span className="text-gray-300">guest</span> in this workspace
          {workspace.guest_expires_at ? <>, until {formatDate(workspace.guest_expires_at)}</> : ''}.
        </p>
        <p>
          {workspace.guest_level === 'full'
            ? 'Because you pay for Mission Control, you have full production access to its shows.'
            : 'You can fill in scripts, notes and graphics text and run the show. With a paid Mission Control account of your own you would get full production access.'}
        </p>
      </div>
    );
  }
  const role = { owner: 'the owner', admin: 'an admin', member: 'a member' }[workspace.your_role] || 'a member';
  return (
    <div className="text-xs text-gray-500">
      You are {role} of <span className="text-gray-300">{workspace.organisation_name}</span>.
    </div>
  );
}

export default function WorkspaceSettings({ onClose }) {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const setAuth = useAuthStore(state => state.setAuth);
  const can = (capability) => !!user?.capabilities?.includes(capability);

  const [workspace, setWorkspace] = useState(null);
  const [allWorkspaces, setAllWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  useEffect(() => {
    async function load() {
      try {
        const [wsRes, mineRes] = await Promise.all([workspaceAPI.get(), workspaceAPI.mine()]);
        setWorkspace(wsRes.data);
        setNameDraft(wsRes.data.name);
        setAllWorkspaces(mineRes.data);
      } catch (err) {
        setError('Failed to load workspace data');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleSwitchWorkspace(e) {
    const workspace_id = e.target.value;
    if (!workspace_id || workspace_id === user?.workspace_id) return;
    try {
      const res = await workspaceAPI.switch(workspace_id);
      setAuth(res.data.token, res.data.user);
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to switch workspace');
    }
  }

  async function handleRename(e) {
    e.preventDefault();
    if (!nameDraft.trim()) return;
    try {
      const res = await workspaceAPI.rename(nameDraft.trim());
      setWorkspace(prev => ({ ...prev, name: res.data.name }));
      setRenaming(false);
      // The header reads the workspace name from the session.
      const me = await authAPI.me();
      const { token, ...fresh } = me.data;
      setAuth(token, fresh);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename workspace');
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') onClose();
    e.stopPropagation();
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4" onKeyDown={handleKeyDown}>
      <div className="bg-[#111111] border border-[#2a2a2a] rounded-lg w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2a2a2a]">
          <h2 className="font-semibold text-white">Workspace Settings</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="px-3 py-2 bg-red-900/30 border border-red-700 rounded text-red-400 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="text-gray-500 text-sm text-center py-4">Loading...</div>
          ) : workspace && (
            <>
              {/* Workspace selector */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Workspace</div>
                {renaming ? (
                  <form onSubmit={handleRename} className="flex gap-2">
                    <input
                      value={nameDraft}
                      onChange={e => setNameDraft(e.target.value)}
                      autoFocus
                      className="flex-1 bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                    />
                    <button type="submit" className="px-3 py-2 bg-blue-700 hover:bg-blue-600 text-white text-xs font-medium rounded">Save</button>
                    <button type="button" onClick={() => setRenaming(false)} className="px-2 text-xs text-gray-500 hover:text-white">Cancel</button>
                  </form>
                ) : allWorkspaces.length > 1 ? (
                  <select
                    value={user?.workspace_id || ''}
                    onChange={handleSwitchWorkspace}
                    className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                  >
                    {allWorkspaces.map(ws => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name}{ws.organisation_name && ws.organisation_name !== ws.name ? ` — ${ws.organisation_name}` : ''}
                        {ws.role === 'guest' ? ' (guest)' : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-white font-medium">{workspace.name}</div>
                )}

                <div className="flex items-center gap-3 mt-2">
                  {can('org:manage') && !renaming && (
                    <button onClick={() => setRenaming(true)} className="text-xs text-gray-500 hover:text-white transition-colors">
                      Rename
                    </button>
                  )}
                  {workspace.your_role !== 'guest' && (
                    <button
                      onClick={() => navigate(`/account?org=${workspace.organisation_id}`)}
                      className="text-xs text-gray-500 hover:text-white transition-colors"
                    >
                      Members, seats and workspaces →
                    </button>
                  )}
                </div>
              </div>

              <AccessSummary workspace={workspace} />

              {can('workspace:guests') && <GuestsSection workspaceId={workspace.id} />}

              {/* vMix instances registry */}
              {can('production:edit') && <VmixInstancesSection />}

              {/* MCP tokens — yours, and they work in every workspace you belong to */}
              <ConnectClaudeSection />

              {!user?.has_account && (
                <div className="text-xs text-gray-500 leading-relaxed">
                  Want to create shows of your own?{' '}
                  <button onClick={() => navigate('/account')} className="text-blue-400 hover:text-blue-300">
                    Set up your own account
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end px-5 py-4 border-t border-[#2a2a2a]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
